import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO from "./SEO";
import Navbar from "./layout/Navbar";
import Footer from "./layout/Footer";
import FloatingActions from "./layout/FloatingActions";
import ImageSlider from "./ui/ImageSlider";
import { elevareCourses, elevareFirstYearPrograms, juniorCurriculum } from "../data/coursesData";
import useFavicon from "../hooks/useFavicon";
import useScroll from "../hooks/useScroll";
import LazyImage from "./ui/LazyImage";
import LazyVideo from "./ui/LazyVideo";

export default function CoursesPage() {
  useFavicon("/logo-main.png");
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const { isScrolled, isVisible } = useScroll(50);
  const [searchQuery, setSearchQuery] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("q") || "";
    } catch (e) {
      return "";
    }
  });
  const [recentSearches, setRecentSearches] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem("cervello_recent_searches");
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (e) {
      console.error("Failed to load recent searches from localStorage:", e);
    }
  }, []);

  // Handle clicking outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inSearch = searchRef.current && searchRef.current.contains(event.target);
      if (!inSearch) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Helper to generate routing slugs from course titles
  const getSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  // Compile and filter academic courses (excluding Business Analytics and Business Lab services)
  const academicJunior = juniorCurriculum.map(c => ({ ...c, type: "junior" }));

  const academicElevare = [
    ...elevareCourses.filter(c => c.title !== "Business Analytics").map(c => ({ ...c, type: "elevare" })),
    ...elevareFirstYearPrograms.map(c => ({ ...c, type: "elevare" }))
  ];

  const allCourses = [...academicJunior, ...academicElevare];

  // Save query to recent searches
  const handleSearchSubmit = (query) => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setRecentSearches((prev) => {
      const filtered = prev.filter((item) => item.toLowerCase() !== trimmed.toLowerCase());
      const updated = [trimmed, ...filtered].slice(0, 5);
      localStorage.setItem("cervello_recent_searches", JSON.stringify(updated));
      return updated;
    });
    setSearchQuery(trimmed);
    setShowDropdown(false);
  };

  // Delete a recent search item
  const handleDeleteRecentSearch = (e, itemToDelete) => {
    e.stopPropagation();
    setRecentSearches((prev) => {
      const updated = prev.filter((item) => item !== itemToDelete);
      localStorage.setItem("cervello_recent_searches", JSON.stringify(updated));
      return updated;
    });
  };

  // Clear all recent searches
  const handleClearAllRecent = (e) => {
    e.stopPropagation();
    setRecentSearches([]);
    localStorage.removeItem("cervello_recent_searches");
  };

  // Get autocomplete suggestions
  const getSuggestions = () => {
    if (!searchQuery.trim()) return { courses: [], tags: [] };
    const query = searchQuery.trim().toLowerCase();

    const matchingCourses = allCourses.filter(c =>
      c.title.toLowerCase().includes(query)
    ).slice(0, 3);

    const matchingTags = [];
    allCourses.forEach(c => {
      const list = c.techs || c.features || [];
      list.forEach(item => {
        if (item.toLowerCase().includes(query) && !matchingTags.includes(item)) {
          matchingTags.push(item);
        }
      });
    });

    return {
      courses: matchingCourses,
      tags: matchingTags.slice(0, 3)
    };
  };

  const suggestions = getSuggestions();

  const renderDropdown = () => {
    if (!showDropdown) return null;
    return (
      <div className="absolute top-full left-0 right-0 mt-2 nm-flat-dark rounded-2xl overflow-hidden z-50 text-left divide-y divide-slate-800/50 border-transparent">

        {/* RECENT SEARCHES PANEL */}
        {!searchQuery.trim() && recentSearches.length > 0 && (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Recent Searches</span>
              <button
                onClick={handleClearAllRecent}
                className="text-[11px] font-semibold text-[#C6A25E] hover:underline"
              >
                Clear All
              </button>
            </div>
            <div className="flex flex-col gap-1">
              {recentSearches.map((item, idx) => (
                <div
                  key={idx}
                  onClick={() => handleSearchSubmit(item)}
                  className="flex items-center justify-between px-3 py-2.5 rounded-xl hover:nm-inset-dark cursor-pointer text-slate-300 hover:text-white text-sm transition-colors group"
                >
                  <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-slate-500">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                    <span>{item}</span>
                  </div>
                  <button
                    onClick={(e) => handleDeleteRecentSearch(e, item)}
                    className="text-slate-500 hover:text-rose-400 p-1 rounded-lg hover:nm-inset-dark opacity-0 group-hover:opacity-100 transition-all duration-200"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
                      <path d="M18 6 6 18M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SUGGESTIONS PANEL */}
        {searchQuery.trim() && (suggestions.courses.length > 0 || suggestions.tags.length > 0) && (
          <div>
            {suggestions.courses.length > 0 && (
              <div className="p-3">
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 mb-2">Course Matches</span>
                <div className="flex flex-col gap-1">
                  {suggestions.courses.map((course, idx) => (
                    <div
                      key={idx}
                      onClick={() => handleSearchSubmit(course.title)}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl hover:nm-inset-dark cursor-pointer text-slate-300 hover:text-white text-sm transition-colors"
                    >
                      {course.images && course.images.length > 0 ? (
                        <LazyImage
                          src={course.images[0]}
                          alt={course.title}
                          className="w-12 h-8 object-cover rounded-lg border border-slate-700/60 bg-slate-800 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-12 h-8 rounded-lg bg-slate-800 flex items-center justify-center border border-slate-700/60 flex-shrink-0 text-sm">
                          {course.icon || "📚"}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate text-white">{course.title}</p>
                        <p className="text-[10px] text-slate-500 capitalize">{course.type} Academy</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {suggestions.tags.length > 0 && (
              <div className="p-3 border-t border-slate-800/80">
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider px-3 mb-2">Topic & Skill Tags</span>
                <div className="flex flex-wrap gap-1.5 px-3">
                  {suggestions.tags.map((tag, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSearchSubmit(tag)}
                      className="px-2.5 py-1 rounded-lg nm-button-dark text-xs font-medium text-slate-300 hover:text-white transition-colors border-transparent"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* NO SUGGESTIONS FOUND */}
        {searchQuery.trim() && suggestions.courses.length === 0 && suggestions.tags.length === 0 && (
          <div className="p-5 text-center text-slate-400 text-xs">
            <p>Press <kbd className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px] text-slate-300 border border-slate-700 font-mono">Enter</kbd> to search for "{searchQuery}"</p>
          </div>
        )}

        {/* DEFAULT EMPTY STATE IN DROPDOWN */}
        {!searchQuery.trim() && recentSearches.length === 0 && (
          <div className="p-5 text-center text-slate-400 text-xs">
            <p>Type to search courses and skills</p>
          </div>
        )}

      </div>
    );
  };

  // Filter based on active tab selection AND search query
  const filteredCourses = allCourses.filter(course => {
    const matchesTab = activeTab === "all" ? true : course.type === activeTab;
    if (!matchesTab) return false;

    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();

    const titleMatch = course.title.toLowerCase().includes(query);
    const descMatch = course.description.toLowerCase().includes(query);
    const list = course.techs || course.features || [];
    const itemMatch = list.some(item => item.toLowerCase().includes(query));

    return titleMatch || descMatch || itemMatch;
  });

  return (
    <React.Fragment>
      <SEO
        title="Academic Coaching & Professional Tech Courses | Cervello Eduverse"
        description="Browse our comprehensive range of concept-first coaching programs at Cervello Junior Academy and professional software track modules at Cervello Elevare Academy."
        keywords="Cervello courses, software training Hyderabad, Intermediate tuition, EAPCET classes, full stack developer bootcamp, data science courses Hyderabad, NEET biology, coding classes, high school coaching"
        url="https://cervelloeduverse.com/courses"
        canonical="https://cervelloeduverse.com/courses"
      />

      <div className="font-inter bg-nm-light text-[#1E293B] min-h-screen relative overflow-x-hidden">
        <Navbar />

        {/* HERO SECTION */}
        <section className="relative pt-20 pb-16 bg-nm-dark text-[#0B1C2D] border-b border-slate-200/20">
          {/* Responsive Brand Video Background */}
          <LazyVideo
            src={isMobile ? "/videos/courses-mobile.mp4" : "/videos/courses-desktop.mp4"}
            poster="/logo-main.webp"
            className="absolute inset-0 w-full h-full object-cover z-0 pointer-events-none select-none opacity-[0.25]"
            placeholderBg="bg-transparent"
          />

          <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000002_1px,transparent_1px),linear-gradient(to_bottom,#00000003_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none z-10" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#C6A25E]/10 rounded-full blur-[120px] translate-y-1/3 translate-x-1/3 pointer-events-none z-10" />

          <div className="relative z-20 max-w-7xl mx-auto px-6 text-center pt-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card-dark border-transparent text-[#0B1C2D] text-xs font-semibold mb-6">
              <span className="w-2 h-2 rounded-full bg-[#C6A25E] animate-pulse" />
              <span>Cervello Academic Portfolio</span>
            </div>
            <h1 className="font-playfair text-4xl sm:text-5xl md:text-6xl font-bold mb-6 tracking-tight text-[#0B1C2D]">
              Our Academic Programs
            </h1>
            <p className="text-slate-700 text-base sm:text-lg max-w-2xl mx-auto font-light mb-8">
              Explore our concept-first coaching curriculums for school and intermediate board prep, and advanced training tracks for developers.
            </p>

            {/* SEARCH BAR CONTAINER */}
            <div ref={searchRef} className="relative max-w-lg mx-auto z-50">
              <div className="relative flex items-center glass-card-dark border-transparent rounded-2xl p-1.5 transition-all duration-300 shadow-lg">
                <span className="pl-3 text-slate-600">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.3-4.3"></path>
                  </svg>
                </span>
                <input
                  type="text"
                  placeholder="Search courses, technologies, or subjects..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSearchSubmit(searchQuery);
                    } else if (e.key === "Escape") {
                      setShowDropdown(false);
                    }
                  }}
                  className="w-full bg-transparent border-0 px-3 py-2 text-[#0B1C2D] placeholder-slate-500 focus:outline-none focus:ring-0 text-sm font-medium"
                />
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setShowDropdown(false);
                    }}
                    className="p-1.5 rounded-full hover:bg-black/10 text-slate-600 hover:text-[#0B1C2D] transition-colors mr-1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                      <path d="M18 6 6 18M6 6l12 12"></path>
                    </svg>
                  </button>
                )}
              </div>

              {/* SEARCH DROPDOWN */}
              {renderDropdown()}
            </div>
          </div>
        </section>

        {/* COURSES FILTER & GRID SECTION */}
        <section className="py-16 max-w-7xl mx-auto px-6">
          {/* Tab Filters */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex nm-inset-light p-1.5 rounded-2xl border-transparent gap-1">
              {[
                { id: "all", label: "All Programs" },
                { id: "junior", label: "Junior Academy" },
                { id: "elevare", label: "Elevare Academy" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 ${activeTab === tab.id
                    ? "nm-flat-light text-[#C6A25E] font-bold border-transparent"
                    : "text-slate-500 hover:text-[#0B1C2D]"
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Grid Layout */}
          {filteredCourses.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredCourses.map((course, index) => {
                const items = course.techs || course.features || [];
                const isJunior = course.type === "junior";
                return (
                  <div
                    key={`${course.title}-${index}`}
                    className="group nm-flat-light rounded-3xl border-transparent hover:-translate-y-1.5 transition-all duration-300 overflow-hidden flex flex-col justify-between h-full animate-slide-up"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <Link
                      to={`/course/${getSlug(course.title)}`}
                      className="relative aspect-video w-full overflow-hidden bg-slate-100 block"
                    >
                      <ImageSlider
                        images={course.images}
                        alt={course.title}
                        className="w-full h-full"
                      />
                      <span className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-md shadow-sm ${(course.available !== false)
                        ? 'bg-emerald-500/95 text-white z-20'
                        : 'bg-amber-500/95 text-white z-20'
                        }`}>
                        {course.status || "Available Now"}
                      </span>
                    </Link>

                    <div className="p-6 flex-1 flex flex-col justify-between">
                      <div>
                        {/* Unified Spacing & Aligned Badges */}
                        <div className="flex items-center justify-between gap-2 mb-4">
                          <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider border-transparent nm-inset-light ${isJunior
                            ? "text-sky-600"
                            : "text-[#C6A25E]"
                            }`}>
                            {isJunior ? "Junior Academy" : "Elevare Academy"}
                          </span>
                          <span className="text-xl leading-none">{course.icon || "📚"}</span>
                        </div>

                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-[9px] font-bold text-slate-400 tracking-wider uppercase">
                            {isJunior ? "SCHOOL & COLLEGE COACHING" : "PROFESSIONAL SOFTWARE SPEC"}
                          </span>
                        </div>
                        <Link to={`/course/${getSlug(course.title)}`}>
                          <h3 className="text-xl font-bold text-[#0B1C2D] mb-2 hover:text-[#C6A25E] transition-colors">
                            {course.title}
                          </h3>
                        </Link>
                        <p className="text-slate-500 text-sm leading-relaxed mb-6 font-light line-clamp-2">
                          {course.description}
                        </p>
                      </div>

                      <div>
                        <ul className="space-y-2 border-t border-slate-200/30 pt-4 mb-6">
                          {items.slice(0, 3).map((item, idx) => (
                            <li key={idx} className="flex items-center text-slate-700 text-xs font-semibold">
                              <span className={`w-1.5 h-1.5 rounded-full mr-2 flex-shrink-0 ${isJunior ? "bg-[#87CEEB]" : "bg-[#C6A25E]"
                                }`} />
                              {item}
                            </li>
                          ))}
                          {items.length > 3 && (
                            <li className="text-slate-400 text-[10px] font-medium tracking-wide pt-1 italic">
                              + {items.length - 3} more modules covered
                            </li>
                          )}
                        </ul>

                        <Link
                          to={`/course/${getSlug(course.title)}`}
                          className="w-full nm-button-light text-[#0B1C2D] font-bold border-transparent py-3 rounded-xl text-center block text-sm"
                        >
                          View Syllabus Details
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 px-6 max-w-md mx-auto nm-flat-light rounded-3xl border-transparent animate-fade-in">
              <div className="w-16 h-16 bg-slate-50 border border-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-[#C6A25E]">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.3-4.3"></path>
                  <line x1="8" y1="11" x2="14" y2="11"></line>
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#0B1C2D] mb-2">No Matching Programs Found</h3>
              <p className="text-slate-500 text-sm mb-6 font-light leading-relaxed">
                We couldn't find any courses matching "{searchQuery}". Try selecting a different tab or checking for typos.
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setActiveTab("all");
                }}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl nm-button-light text-[#0B1C2D] font-bold border-transparent transition-colors"
              >
                Clear Search & Filters
              </button>
            </div>
          )}
        </section>

        <Footer />
        <FloatingActions />
      </div>
    </React.Fragment>
  );
}
